import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableIconCell,
  TableDots,
  Pagination,
  Chip,
  SimpleTable,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../context';
import { filterWithUserId, momentTextFormat, showToRoles } from '../../utils';
import { useStatesWithFilterCounter, usePaginationState, useSalesFeesQuery } from '../../hooks';
import { saveCache } from '../../services';
import {
  salesFeeActionMenuMissingOptions,
  salesFeeActionMenuOptions,
  salesFeeDotsViewActions,
  SalesFeeStatusToLabels,
  SalesFeeStatusToChipColors,
  TABLE,
} from '../../const';
import { SalesFeeGetAllResponse } from '../../types';
import { i18n } from '../../i18n';

interface SalesFeesPageProps {
  innerScroll?: boolean;
}

export const SalesFeesPage = styled.div<SalesFeesPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

interface SalesFeesHeaderProps {
  totalCount: number;
}

export const SalesFeesHeader = ({ totalCount }: SalesFeesHeaderProps) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.salesFees.title', { totalCount })}
      </Typography>
    </DashboardHeader>
  );
};

interface SalesFeesBodyProps {
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
}

export const SalesFeesBody = ({ setTotalCount }: SalesFeesBodyProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState<null | number>(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    country: '',
    city: '',
    yardId: '',
    clientId: '',
  });

  const { data = { data: [], totalCount: 0 }, isFetching } = useSalesFeesQuery({
    ...filterWithUserId(filteredStates, user),
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleDotsToggle = (i: number) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handlePageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (
    row: SalesFeeGetAllResponse['data'][number],
    selectedOption: string,
    id: string,
  ) => {
    if (selectedOption === 'addFee') {
      saveCache('sales-fee-add', {
        yardName: row.name,
      });
      navigate(`/dashboard/sales-fees/add/${row.id}`);
    }
    if (selectedOption === 'viewFee') {
      navigate(`/dashboard/sales-fees/view/${id}`);
    }
  };

  const handleRowClick = (to: string, feeStatus: string) => {
    if (feeStatus === 'MISSING') return;
    if (openDotsMenuIndex !== null) return;
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      tableRowProps={(row, i) => ({
        key: 'sales-fee' + i,
        onClick: () =>
          handleRowClick(`/dashboard/sales-fees/view/${row.salesFee.id}`, row.salesFee.feeStatus),
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.actions'),
          body: (row, i) =>
            showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user) ||
            row.salesFee.feeStatus === 'ACTIVE' ? (
              <TableIconCell component={'th'} align={'left'}>
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={
                    row.salesFee.feeStatus === 'MISSING'
                      ? showToRoles(
                          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
                          user,
                        )
                        ? salesFeeActionMenuMissingOptions
                        : []
                      : showToRoles(
                          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
                          user,
                        )
                      ? salesFeeActionMenuOptions
                      : salesFeeDotsViewActions
                  }
                  onSelect={(selectedOption) => {
                    handleDotsOptionSelect(
                      row as SalesFeeGetAllResponse['data'][number],
                      selectedOption,
                      row.salesFee.id,
                    );
                  }}
                />
              </TableIconCell>
            ) : (
              <TableIconCell component={'th'} align={'left'} />
            ),
        },
        {
          headText: () => i18n.t('label.yardName'),
          bodyText: (row) => row.name,
        },
        {
          headText: () => i18n.t('common.yardCompany'),
          bodyText: (row) => row.yardCompany.legalName,
          isHidden: () => !showToRoles(['SUPER_ADMIN'], user),
        },
        {
          headText: () => i18n.t('label.feeStatus'),
          body: (row) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={SalesFeeStatusToLabels[row.salesFee.feeStatus]}
                color={SalesFeeStatusToChipColors[row.salesFee.feeStatus]}
              />
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.effectiveDate'),
          bodyText: (row) =>
            row.salesFee.activationDate ? momentTextFormat(row.salesFee.activationDate, user) : '',
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
